import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { m as motion, LazyMotion, domAnimation, useMotionValue, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Check, Dumbbell, Flag, Heart, Pill, Utensils } from "lucide-react";
import { GAMEPOWER_EVENTS, getGamepowerEventPosition, getGamepowerScrollState } from "../../lib/landingGamepowerScroll";

const icons = { utensils: Utensils, dumbbell: Dumbbell, heart: Heart, pill: Pill };
const WHITE = "#F5F5F2";
const CYAN = "#00F0FF";

// A short, reversible light envelope around an existing event contact.
// It changes presentation only; the score, path and phase model stay untouched.
function contactImpulse(progress, at) {
  const elapsed = progress - at;
  if (elapsed <= 0 || elapsed >= 0.055) return 0;
  const t = elapsed < 0.009 ? elapsed / 0.009 : 1 - (elapsed - 0.009) / 0.046;
  return t * t * (3 - 2 * t);
}

function useHeaderHeight(container) {
  const [height, setHeight] = useState(84);
  useLayoutEffect(() => {
    const header = container.current?.closest(".pc-landing")?.querySelector(".pc-landing-header");
    if (!header) return undefined;
    const measure = () => {
      const next = Math.ceil(header.getBoundingClientRect().height);
      if (next > 0) setHeight((previous) => previous === next ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(header);
    window.addEventListener("resize", measure, { passive: true });
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [container]);
  return height;
}

function TimelineEvent({ event, index, state }) {
  const row = useRef(null);
  const opacity = useTransform(state, (value) => value.events[index].opacity);
  const y = useTransform(opacity, [0, 1], [7, 0]);
  const activation = useTransform(state, (value) => value.events[index].activation);
  const color = useTransform(activation, [0, 1], [WHITE, CYAN]);
  const scale = useTransform(opacity, [0, 1], [0.96, 1]);
  const waitingOpacity = useTransform(activation, [0, 1], [1, 0]);
  const impulse = useTransform(state, (value) => contactImpulse(value.progress, event.at));
  const impulseScale = useTransform(impulse, [0, 1], [0.75, 1.35]);
  const Icon = icons[event.icon];
  const syncStatus = useCallback((value) => {
    if (row.current) row.current.dataset.state = value.events[index].activated ? "active" : "planned";
  }, [index]);
  useMotionValueEvent(state, "change", syncStatus);
  useEffect(() => { syncStatus(state.get()); }, [state, syncStatus]);
  return (
    <motion.li ref={row} className="pc-gp-event" data-event={event.id} style={{ top: `${getGamepowerEventPosition(event)}%`, opacity, y }}>
      <motion.span className="pc-gp-event__marker" style={{ color }} aria-hidden="true">
        <motion.span className="pc-gp-event__impulse" style={{ opacity: impulse, scale: impulseScale }} />
      </motion.span>
      <motion.span className="pc-gp-event__icon" style={{ color, scale }} aria-hidden="true"><Icon size={22} strokeWidth={1.5} /></motion.span>
      <div className="pc-gp-event__copy">
        <motion.span className="pc-gp-event__name" style={{ color }}>{event.label}</motion.span>
        <span className="pc-gp-event__status" aria-hidden="true">
          <motion.span className="pc-gp-sr-only" style={{ opacity: waitingOpacity }}>Im Plan</motion.span>
          <motion.span className="pc-gp-event__done" style={{ opacity: activation }}><Check size={11} strokeWidth={1.7} /><span className="pc-gp-sr-only">Umgesetzt</span></motion.span>
        </span>
      </div>
    </motion.li>
  );
}

function GamepowerStage({ progress, isStatic = false }) {
  const state = useTransform(progress, getGamepowerScrollState);
  const instrument = useRef(null);
  const number = useRef(null);
  const source = useRef(null);
  const stage = useRef(null);
  const energy = useTransform(state, (value) => value.energy);
  const edgeX = useTransform(energy, (value) => `${value * 100}%`);
  const edgeOpacity = useTransform(energy, [0, 0.015], [0, 1]);
  const impulse = useTransform(state, (value) => Math.max(...GAMEPOWER_EVENTS.map((event) => contactImpulse(value.progress, event.at))));
  const tension = useTransform(progress, [0.80, 0.82, 0.86, 0.89], [0, 1, 1, 0]);
  const contactLight = useTransform(() => Math.max(impulse.get(), tension.get() * 0.24));
  const startOpacity = useTransform(progress, [0, 0.085, 0.17], [1, 1, 0]);
  const startY = useTransform(startOpacity, [0, 1], [-4, 0]);
  const path = useTransform(state, (value) => value.activePathProgress);
  const tipY = useTransform(path, (value) => `${value * 100}%`);
  const tipOpacity = useTransform(path, [0, 0.015], [0, 1]);
  const matchday = useTransform(state, (value) => value.matchdayOpacity);
  const matchdayY = useTransform(matchday, [0, 1], [6, 0]);
  const sync = useCallback((value) => {
    // Only text/ARIA/diagnostic attributes change here; React does not render on scroll.
    if (number.current && number.current.textContent !== String(value.percentage)) number.current.textContent = String(value.percentage);
    if (instrument.current?.getAttribute("aria-valuenow") !== String(value.percentage)) instrument.current?.setAttribute("aria-valuenow", value.percentage);
    const latestEvent = value.events.filter((event) => event.activated).at(-1);
    const sourceLabel = latestEvent?.label || "Spielzyklus";
    if (source.current && source.current.textContent !== sourceLabel) source.current.textContent = sourceLabel;
    if (stage.current) {
      stage.current.dataset.progress = value.progress.toFixed(5);
      stage.current.dataset.phase = value.phase;
    }
  }, []);
  useMotionValueEvent(state, "change", sync);
  useEffect(() => { sync(state.get()); }, [state, sync]);

  return (
    <div ref={stage} className="pc-gp-scene" data-gamepower-scene>
      <div className="pc-gp-instrument">
        <div className="pc-gp-instrument__heading"><span>Gamepower</span><a className="pc-gp-skip" href="#app">Weiter zur App <ArrowDown size={13} aria-hidden="true" /></a></div>
        <div ref={instrument} role="meter" aria-label="Gamepower – illustrative Planumsetzung" aria-valuemin={0} aria-valuemax={100} aria-valuenow={isStatic ? 100 : 0} aria-describedby="pc-gp-meaning">
          <motion.div className="pc-gp-energy" style={{ "--gp-energy": energy }} aria-hidden="true">
            <div className="pc-gp-energy__well">
              <motion.div className="pc-gp-energy__fill" style={{ width: edgeX }}>
                <motion.div className="pc-gp-energy__impulse" style={{ opacity: impulse }} />
              </motion.div>
              <motion.div className="pc-gp-energy__edge" style={{ x: edgeX, opacity: edgeOpacity }}>
                <span /><motion.i className="pc-gp-energy__contact-light" style={{ opacity: contactLight }} />
              </motion.div>
            </div>
            <div className="pc-gp-value"><span ref={number} data-gamepower-value>{isStatic ? 100 : 0}</span><span className="pc-gp-value__unit">%</span></div>
            <span className="pc-gp-energy__source" ref={source}>{isStatic ? "Supplements" : "Spielzyklus"}</span>
          </motion.div>
        </div>
      </div>

      <div className="pc-gp-timeline">
        <motion.div className="pc-gp-timeline__start" style={{ opacity: startOpacity, y: startY }}><span className="pc-gp-timeline__origin" aria-hidden="true" /><span>Spiel beendet</span></motion.div>
        <div className="pc-gp-timeline__track">
          <div className="pc-gp-path" aria-hidden="true"><motion.div className="pc-gp-path__active" style={{ scaleY: path }} /><motion.div className="pc-gp-path__tip" style={{ y: tipY, opacity: tipOpacity }}><span /></motion.div></div>
          <ol aria-label="Maßnahmen im illustrativen Spielzyklus">{GAMEPOWER_EVENTS.map((event, index) => <TimelineEvent key={event.id} event={event} index={index} state={state} />)}</ol>
        </div>
      </div>

      <motion.div className="pc-gp-matchday" style={{ opacity: matchday, y: matchdayY }} aria-hidden="true">
        <p>Plan vollständig umgesetzt</p><div>Spieltag <Flag size={24} strokeWidth={1.2} /></div>
      </motion.div>
      <p id="pc-gp-meaning" className="pc-gp-sr-only">Illustrativer Spielzyklus: Nach dem Spiel beginnt Gamepower bei 0 Prozent. Das Ziel bis zum nächsten Spiel ist die vollständige Umsetzung des individuellen Plans bei 100 Prozent. Die Maßnahmen sind Frühstück, Training, Mittagessen, Recovery, Abendessen und Supplements. Danach folgt der Spieltag. Gamepower ist kein medizinischer Readiness-Score und keine Aussage über körperliche Fitness oder Einsatzfähigkeit.</p>
    </div>
  );
}

function GamepowerLayout({ intro, children }) {
  return intro ? <div className="pc-gp-desktop-stage">{intro}{children}</div> : children;
}

function AnimatedGamepower({ intro }) {
  const container = useRef(null);
  const headerHeight = useHeaderHeight(container);
  const { scrollYProgress } = useScroll({ target: container, offset: [`start ${headerHeight + 12}px`, "end end"] });
  return <div ref={container} className="pc-gp-scroll" data-gamepower-mode="scroll" style={{ "--gp-header-height": `${headerHeight}px` }}><GamepowerLayout intro={intro}><GamepowerStage progress={scrollYProgress} /></GamepowerLayout></div>;
}

function StaticGamepower({ intro }) {
  const progress = useMotionValue(1);
  return <div className="pc-gp-scroll pc-gp-scroll--static" data-gamepower-mode="static"><GamepowerLayout intro={intro}><GamepowerStage progress={progress} isStatic /></GamepowerLayout></div>;
}

export default function LandingGamepowerScroll({ intro }) {
  // Keep the mobile intro outside the scroll target; desktop shares one sticky stage.
  const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia?.("(min-width: 1101px)").matches));
  useEffect(() => {
    const query = window.matchMedia?.("(min-width: 1101px)");
    if (!query) return undefined;
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  // Subscribe directly: this installed Motion version only reads the initial preference.
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches));
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return undefined;
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return <>{!desktop && intro}<LazyMotion features={domAnimation} strict>{reducedMotion ? <StaticGamepower intro={desktop ? intro : null} /> : <AnimatedGamepower intro={desktop ? intro : null} />}</LazyMotion></>;
}
